import { Power1, Power3, TweenMax } from 'gsap';
import { Container, Sprite } from 'pixi.js';
import { getGameSize } from '../../../Config/Levels';
import Core, { GameOrientation } from '../../../Core/Core';
import SoundManager from '../../../Sound/SoundManager';
import { Collection } from '../../../Tiles/TileSet';
import { createOutlineText } from '../../../Utils/Utils';
import { FlavorText } from '../../Animations/FlavorText';
import DEG_TO_RAD = PIXI.DEG_TO_RAD;
import { MovesCounter } from '../Shared/MovesCounter';
import { CircularBar } from './CircularBar';
import { ComboMeter } from './ComboMeter';
import { LevelGoalDisplay } from './LevelGoalDisplay';
import { LevelTimer } from './LevelTimer';
import { StarsMeter } from './StarsMeter';

export class TopUi extends Container {
    public centerBar: CircularBar;
    public bg: PIXI.Sprite;
    public readonly movesCounter: MovesCounter;
    private starsMeter: StarsMeter;
    private flavorTexts: FlavorText[] = [];
    private readonly goalDisplay: LevelGoalDisplay;
    private readonly flavorText: PIXI.Text;
    private readonly comboText: PIXI.Text;
    private readonly flavorScale: number;
    private readonly comboScale: number;

    // NODE_ENV === 'editor'
    private scoreText: PIXI.Text;

    constructor(winTime: number) {
        super();

        const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
        const bgId = isPortrait ? 'top-hud-bg' : 'vertical-bg';
        const bg = new Sprite(PIXI.utils.TextureCache['gameui/' + bgId]);
        if (!isPortrait) {
            bg.scale.set(1.35);
        }
        bg.interactive = true;
        this.bg = bg;
        this.addChild(bg);

        const starsMeter = new StarsMeter(isPortrait);
        if (isPortrait) {
            starsMeter.x = bg.width * 0.073;
            starsMeter.y = bg.height * 0.23;
        } else {
            starsMeter.scale.set(-1, 1);
            starsMeter.rotation = DEG_TO_RAD * 90;
            starsMeter.x = bg.width * 0.5 + starsMeter.height * 0.5;
            starsMeter.y = bg.height * 0.07 + Math.abs(starsMeter.width);
        }

        this.centerBar = winTime <= 0 ? new ComboMeter() : new LevelTimer(winTime);
        let comboMeter: PIXI.Container = this.centerBar;
        if (isPortrait) {
            comboMeter.x = bg.width / 2;
            comboMeter.y = bg.height * 0.55;
        } else {
            const comboBg = new Sprite(PIXI.utils.TextureCache['gameui/tophud-combo-bg']);
            comboBg.anchor.set(0.5);
            comboBg.scale.set(1.2);
            comboBg.x = bg.width / 2;
            comboBg.y = bg.height * 0.57;
            comboBg.addChild(comboMeter);
            comboMeter = comboBg;
        }

        const goalWidth = isPortrait ? bg.width * 0.32 : bg.width * 0.8;
        const goalHeight = isPortrait ? bg.height * 0.32 : bg.height * 0.1;
        this.goalDisplay = new LevelGoalDisplay('pair', 10, goalWidth, goalHeight);
        let goalDisplay: PIXI.Container = this.goalDisplay;
        if (isPortrait) {
            goalDisplay.x = bg.width * 0.67;
            goalDisplay.y = bg.height * 0.25;
        } else {
            goalDisplay.x = bg.width * 0.5 - goalDisplay.width * 0.6;
            goalDisplay.y = bg.height * 0.68;
            const container = new Container();
            const goalBg = new Sprite(PIXI.utils.TextureCache['gameui/tophud-goal-bg']);
            goalBg.scale.set(0.45);
            goalBg.x = goalDisplay.x - 1;
            goalBg.y = goalDisplay.y + 4;
            container.addChild(goalBg, goalDisplay);
            goalDisplay = container;
        }

        const flavorText = createOutlineText(
            '',
            new PIXI.Rectangle(0, 0, bg.width * 0.6, bg.height * 0.5),
            'Baloo',
            45
        );
        if (isPortrait) {
            flavorText.x = bg.x + bg.width / 2;
            flavorText.y = bg.y + bg.height * 1.1;
        } else {
            const flavPt = this.toLocal(
                new PIXI.Point(getGameSize().gameWidth * 0.5 - flavorText.width * 0.5, 50)
            );
            flavorText.x = flavPt.x;
            flavorText.y = flavPt.y;
        }

        flavorText.alpha = 0;
        this.flavorScale = flavorText.scale.x;

        const comboText = createOutlineText(
            '',
            new PIXI.Rectangle(0, 0, bg.width * 0.6, bg.height * 0.5),
            'Baloo',
            38
        );

        if (isPortrait) {
            comboText.x = bg.x + bg.width / 2;
            comboText.y = bg.y + bg.height * 1.35;
        } else {
            const comboPt = this.toLocal(
                new PIXI.Point(getGameSize().gameWidth * 0.5 - comboText.width * 0.5, 100)
            );
            comboText.x = comboPt.x;
            comboText.y = comboPt.y;
        }
        comboText.alpha = 0;
        this.comboScale = comboText.scale.x;

        this.addChild(bg, starsMeter, comboMeter, goalDisplay, flavorText, comboText);

        if (!isPortrait) {
            const movesCounter = (this.movesCounter = new MovesCounter(
                bg.width,
                bg.height,
                isPortrait
            ));
            movesCounter.x = bg.width * 0.5;
            movesCounter.y = bg.height * 0.82;
            this.addChild(movesCounter);
        }

        this.starsMeter = starsMeter;
        this.flavorText = flavorText;
        this.comboText = comboText;

        if (NODE_ENV === 'editor') {
            this.scoreText = createOutlineText(
                '0',
                new PIXI.Rectangle(0, 0, bg.width, bg.height),
                'Baloo',
                38
            );
            this.scoreText.x = bg.width * 0.5;
            this.scoreText.y = bg.y + this.scoreText.height * 0.5;

            this.addChild(this.scoreText);
        }
    }

    public getGoalDisplay() {
        return this.goalDisplay;
    }

    public initTopUi(
        goalId: string,
        target: number,
        starTiers: number[],
        maxScore: number,
        timedLevel: boolean,
        collection: Collection,
        score: number
    ) {
        this.goalDisplay.updateGoalIcon(goalId, collection);
        this.goalDisplay.updateLevelGoal(target);
        this.starsMeter.showStarPercents(starTiers, maxScore);
        if (timedLevel) {
            this.setTime(score, false);
        }
    }

    public startLevelTimer() {
        const levelTimer = this.centerBar as LevelTimer;
        this.starsMeter.startTime();
        levelTimer.started = true;
    }

    public showScore(score: number, timedLevel?: boolean) {
        if (NODE_ENV === 'editor') {
            this.scoreText.text = score.toString();
        }
        this.starsMeter.showScoreProgress(score, timedLevel);
    }

    public showGoal(goalLeft: number) {
        this.goalDisplay.updateLevelGoal(goalLeft);
    }

    public showCombo(multiplier: number, oldMultiplier: number) {
        const comboMeter = this.centerBar as ComboMeter;
        comboMeter.updateComboNum(multiplier, oldMultiplier);
    }

    public setTime(winTime: number, start: boolean = true) {
        const levelTimer = this.centerBar as LevelTimer;
        levelTimer.started = false;
        levelTimer.setTime(winTime);
        this.showScore(winTime, true);
        if (start) {
            this.startLevelTimer();
        }
    }

    public update() {
        if (this.centerBar) {
            this.centerBar.tick(false);
        }
    }

    public showFlavorText(score: number) {
        // hacky...but what are you gonna do at this point
        if (Core.sdk.frozen) {
            return;
        }
        if (score > 3) {
            let text = '';
            if (score < 7) {
                text = Core.local.getText('great');
            } else if (score < 10) {
                text = Core.local.getText('awesome');
            } else {
                text = Core.local.getText('amazing');
            }
            const flav1 = new FlavorText(this.comboScale);
            const flav1Sprite = flav1.sprite;
            const flav2 = new FlavorText(this.flavorScale);
            const flav2Sprite = flav2.sprite;
            flav1Sprite.anchor.set(0.5);
            flav2Sprite.anchor.set(0.5);

            flav1.play();
            flav2.play();
            SoundManager.playSound('FloaterText');
            this.addChild(flav1Sprite, flav2Sprite);

            this.flavorTexts = [flav1, flav2];

            this.flavorText.text = text;
            this.comboText.text = Core.local.getText('scoreX') + 'x' + score;
            this.flavorText.anchor.set(0.5);
            this.comboText.anchor.set(0.5);

            const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
            if (isPortrait) {
                this.comboText.y = this.bg.y + this.bg.height * 2;
                this.flavorText.y = this.bg.y + this.bg.height * 1.75;
            } else {
                const { gameWidth } = getGameSize();
                const flavorPosition = gameWidth / 2;
                const comboPosition = flavorPosition;
                this.flavorText.position = this.toLocal(
                    new PIXI.Point(flavorPosition, 50),
                    Core.component
                );
                this.comboText.position = this.toLocal(
                    new PIXI.Point(comboPosition, 100),
                    Core.component
                );
            }

            flav1Sprite.x = this.comboText.x;
            flav2Sprite.x = this.flavorText.x;
            flav1Sprite.y = this.comboText.y;
            flav2Sprite.y = this.flavorText.y;

            const endFlavPt = this.flavorText.y - this.flavorText.height;

            const endCombo = endFlavPt + this.flavorText.height / 2 + this.comboText.height / 2;

            const midFlavY = isPortrait ? this.bg.y + this.bg.height * 1.1 : endFlavPt * 0.75;
            const midComboY = isPortrait ? this.bg.y + this.bg.height * 1.35 : endCombo * 0.75;

            const endFlavY = isPortrait ? this.bg.y + this.bg.height * 0.4 : endFlavPt;
            const endComboY = isPortrait ? this.bg.y + this.bg.height * 0.6 : endCombo;

            TweenMax.to([this.flavorText, this.comboText], 0.4, {
                alpha: 1,
                onComplete: () => {
                    TweenMax.to([this.flavorText, this.comboText], 0.4, {
                        alpha: 0,
                        delay: 1,
                        ease: Power1.easeOut,
                    });
                },
            });
            TweenMax.to([this.flavorText, flav2], 0.4, {
                onComplete: () => {
                    TweenMax.to(this.flavorText, 0.4, {
                        delay: 1,
                        y: endFlavY,
                    });
                },
                ease: Power3.easeOut,
                y: midFlavY,
            });
            TweenMax.to([this.comboText, flav1], 0.4, {
                ease: Power3.easeOut,
                onComplete: () => {
                    TweenMax.to(this.comboText, 0.4, {
                        delay: 1,
                        y: endComboY,
                    });
                },
                y: midComboY,
            });
        }
    }

    public setPaused(paused: boolean) {
        this.centerBar.setPaused(paused);
    }

    public cleanUp() {
        this.flavorTexts.forEach(t => {
            TweenMax.killTweensOf(t);
        });
        this.flavorTexts = [];
        this.centerBar.cleanUp();
        this.starsMeter.cleanUp();
        this.goalDisplay.cleanUp();
        TweenMax.killTweensOf(this.flavorText);
        this.flavorText.destroy();
        TweenMax.killTweensOf(this.comboText);
        this.comboText.destroy();
    }
}
